import { ExclusiveLensStatus, type GoogleProtobufTimestamp } from "../../gql-generated/types";
import { type PayoutDateRange, truncateDateToUTC12AMTime } from "../converters";

export interface ClampedLensPlusRange<T> {
    range: T;
    isValid: boolean;
}

const timestampToMilliseconds = ({ seconds, nanos }: GoogleProtobufTimestamp): number =>
    seconds * 1000 + nanos / 1_000_000;

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const exclusivityUTCStartMilliseconds = (markedAsExclusiveAt: GoogleProtobufTimestamp): number =>
    truncateDateToUTC12AMTime(new Date(timestampToMilliseconds(markedAsExclusiveAt)));

export const getLensPlusExclusivityCutoff = (
    exclusiveLensStatus: ExclusiveLensStatus | undefined,
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null,
): GoogleProtobufTimestamp | undefined =>
    exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive
        ? markedAsExclusiveAt ?? undefined
        : undefined;

export const clampLensPlusPayoutDateRange = (
    range: PayoutDateRange,
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null,
): ClampedLensPlusRange<PayoutDateRange> => {
    if (!markedAsExclusiveAt) {
        return { range, isValid: true };
    }

    const markedAtMilliseconds = exclusivityUTCStartMilliseconds(markedAsExclusiveAt);
    const startMilliseconds = range.startDate ? Date.parse(range.startDate) : Number.NEGATIVE_INFINITY;
    const clampedStartMilliseconds = Math.max(startMilliseconds, markedAtMilliseconds);
    const endMilliseconds = range.endDate
        ? Date.parse(range.endDate)
        : truncateDateToUTC12AMTime(new Date()) + ONE_DAY_IN_MS;

    return {
        range: {
            ...range,
            startDate: new Date(clampedStartMilliseconds).toISOString(),
            endDate: range.endDate ?? new Date(endMilliseconds).toISOString(),
        },
        isValid: clampedStartMilliseconds < endMilliseconds,
    };
};
